/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';

import { DocumentUpload } from 'App/Pages/Project/IdpApplication/idp-project/idp-extraction-project/document-upload';
import { idpApplicationStore, structuredExtractionStore } from 'stores';
import { getIdFromSlug } from 'utils/getIdFromSlug';

export const TestTemplate = observer(({ canUserModifyProject }) => {
  const params = useParams();
  const slug = params['slug'] || '';
  const idpProjectId = getIdFromSlug(slug);

  const {
    testTemplateDocuments,
    testTemplateIsExtracting,
    testTemplateIncludedFieldsIds,
    extractTestTemplate,
    deleteTestDocument,
    uploadDocumentForExtraction
  } = structuredExtractionStore;

  const handleDocumentUpload = async (files) => {
    await uploadDocumentForExtraction(idpProjectId, files);
  };

  const handleDeleteDocument = async (documentId) => {
    await deleteTestDocument(documentId);
  };

  const handleExtractClick = () => {
    extractTestTemplate(
      idpProjectId,
      idpApplicationStore.idpApplication.clusterId,
      testTemplateDocuments.map((doc) => doc.id)
    );
  };

  const canProceedWithExtraction = () => {
    return (
      testTemplateDocuments.length > 0 &&
      !testTemplateIsExtracting &&
      canUserModifyProject &&
      testTemplateIncludedFieldsIds.length > 0
    );
  };

  return (
    <DocumentUpload
      multiple
      canUserModifyProject={canUserModifyProject}
      documents={testTemplateDocuments}
      onExtractClick={handleExtractClick}
      canProceedWithExtraction={canProceedWithExtraction()}
      isExtracting={testTemplateIsExtracting}
      labels={{
        titleText: 'Test template',
        descriptionText: 'Drag and drop the document you want to use as a template for structured document extraction.',
        dropzoneLabelText: 'Drag and drop files here or click to upload a document',
        extractButtonText: 'Test extraction template',
        extractingInProgressText: 'Extracting document...'
      }}
      onFilesUpload={handleDocumentUpload}
      onDeleteDocument={handleDeleteDocument}
    />
  );
});
